import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from 'react-router';
import { ApiError } from '@estudio/contracts';
import { ServicesProvider, type Services } from '../core/services.tsx';
import { SessionGate } from '../core/session.tsx';
import { registry } from '../core/registry.ts';
import { ModuleView } from '../core/ModuleBoundary.tsx';
import { ToastProvider } from '../ui/index.tsx';
import { LegalGate } from '../shared/LegalGate.tsx';
import { Layout } from './Layout.tsx';
import {ChannelScopeProvider} from '../core/channelScope.tsx';
import { NotFound } from './NotFound.tsx';

/** Rutas construidas desde el registro de módulos: ningún módulo se importa aquí. */
export function buildRoutes(): RouteObject[] {
  const top = registry.filter((m) => m.path).map<RouteObject>((m) => ({ path: m.path!, element: <ModuleView id={m.id} title={m.title} load={m.load} /> }));
  const layoutModule = registry.find((m) => m.projectLayout);
  const tabs = registry.filter((m) => m.projectTab).sort((a, b) => a.projectTab!.order - b.projectTab!.order);
  const projectRoute: RouteObject[] = layoutModule
    ? [{
        path: 'proyectos/:projectId',
        element: <ModuleView id={`${layoutModule.id}-layout`} title={layoutModule.title} load={layoutModule.projectLayout!} />,
        children: [
          ...(tabs[0] ? [{ index: true, element: <Navigate to={tabs[0].projectTab!.segment} replace /> }] : []),
          ...tabs.map<RouteObject>((m) => ({ path: m.projectTab!.segment, element: <ModuleView id={m.id} title={m.title} load={m.load} /> })),
        ],
      }]
    : [];
  const home = registry.find((m) => m.id === 'home')?.path ?? top[0]?.path ?? 'ajustes';
  return [
    {
      path: '/',
      element: (
        <SessionGate>
          <LegalGate><ChannelScopeProvider><Layout /></ChannelScopeProvider></LegalGate>
        </SessionGate>
      ),
      children: [{ index: true, element: <Navigate to={`/${home}`} replace /> }, ...top, ...projectRoute, { path: '*', element: <NotFound /> }],
    },
  ];
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        retry: (count, e) => (e instanceof ApiError ? e.retryable && count < 2 : count < 2),
      },
      mutations: { retry: false },
    },
  });
}

export function App({ services }: { services: Services }) {
  const router = createBrowserRouter(buildRoutes());
  return (
    <ServicesProvider services={services}>
      <QueryClientProvider client={createQueryClient()}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </ServicesProvider>
  );
}
